import { Icon } from '@iconify/react'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.6);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 9999;
   opacity: 0;
   transition: opacity 0.2s ease;
`

const Modal = styled.div`
   background: white;
   border-radius: 16px;
   width: 90%;
   max-width: 420px;
   overflow: hidden;
   text-align: center;
`

const Header = styled.div`
   padding: 32px 24px 16px;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 16px;
`

const IconWrap = styled.div`
   width: 56px;
   height: 56px;
   border-radius: 50%;
   background: #FEF2F2;
   display: flex;
   align-items: center;
   justify-content: center;
   color: #DC2626;
`

const Content = styled.div`
   padding: 0 24px 24px;
`

const Title = styled.h3`
   font-size: 18px;
   font-weight: 600;
   color: #18181B;
   margin: 0 0 8px 0;
`

const Description = styled.p`
   font-size: 14px;
   color: #71717A;
   margin: 0;
   line-height: 1.5;
`

const Footer = styled.div`
   padding: 16px 24px 24px;
   display: flex;
   gap: 12px;
`

const Button = styled.button`
   flex: 1;
   padding: 12px 24px;
   border-radius: 999px;
   font-size: 14px;
   font-weight: 600;
   cursor: pointer;
   border: 0;
   transition: all 0.15s ease;

   &.primary {
      background: #DC2626;
      color: white;

      &:hover {
         background: #B91C1C;
      }
   }

   &.secondary {
      background: #F4F4F5;
      color: #18181B;

      &:hover {
         background: #E4E4E7;
      }
   }
`

interface SessionExpiredModalProps {
   open: boolean
   onVerifyPin?: () => void
   onLoginAgain?: () => void
   showPinOption?: boolean
}

export default function SessionExpiredModal({ open, onVerifyPin, onLoginAgain, showPinOption = false }: SessionExpiredModalProps): React.JSX.Element | null {
   const overlayRef = useRef<HTMLDivElement>(null)

   useEffect(() => {
      if (open && overlayRef.current) {
         overlayRef.current.style.display = 'flex'
         setTimeout(() => {
            if (overlayRef.current) overlayRef.current.style.opacity = '1'
         }, 10)
      }
   }, [open])

   if (!open) return null

   const handleLogin = () => {
      if (onLoginAgain) {
         onLoginAgain()
      } else {
         window.location.href = '/login'
      }
   }

   const handleVerifyPin = () => {
      if (onVerifyPin) {
         onVerifyPin()
      }
   }

   return (
      <Overlay ref={overlayRef}>
         <Modal>
            <Header>
               <IconWrap>
                  <Icon icon="solar:clock-circle-outline" width={28} />
               </IconWrap>
               <Title>Session Expired</Title>
            </Header>
            <Content>
               <Description>
                  {showPinOption 
                     ? 'Your session has expired due to inactivity. Please verify your PIN or log in again to continue.'
                     : 'Your session has expired due to invalid credentials. Please log in again to continue accessing your account.'}
               </Description>
            </Content>
            <Footer>
               {showPinOption && (
                  <Button className="secondary" onClick={handleVerifyPin}>
                     Verify PIN
                  </Button>
               )}
               <Button className="primary" onClick={handleLogin}>
                  Log In Again
               </Button>
            </Footer>
         </Modal>
      </Overlay>
   )
}
